import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronDown, Cpu, Zap, Sun, Droplet, Thermometer, Home as HomeIcon, Building2, Hammer, Wrench, Lightbulb, Shield } from "lucide-react";
import ProductCard from "../components/ProductCard";
import EnergyCalculator from "../components/EnergyCalculator";
import InteractiveHouse from "../components/InteractiveHouse";
import Testimonials from "../components/Testimonials";
import ReferenceGallery from "../components/ReferenceGallery";
import FAQ from "../components/FAQ";
import { fetchProducts } from "../lib/api";
import { useLang } from "../context/I18nContext";
import { HERO_VIDEO } from "../config/sectionVideos";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const { t } = useLang();

  useEffect(() => {
    fetchProducts({ featured: true }).then(setFeatured).catch(() => {});
  }, []);

  // Rotating hero headline — 3 positioning angles, 5s each
  const HERO_PHRASES = [
    { line: "Haustechnik", italic: "aus einer Hand." },
    { line: "BIM von", italic: "Anfang an." },
    { line: "Smart", italic: "gesteuert." },
  ];
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % HERO_PHRASES.length), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const phrase = HERO_PHRASES[phraseIdx];

  const segments = [
    { icon: HomeIcon, label: t("seg.apartment") },
    { icon: Building2, label: t("seg.efh") },
    { icon: Building2, label: t("seg.mfh") },
    { icon: Building2, label: t("seg.complex") },
    { icon: Hammer, label: t("seg.new") },
    { icon: Wrench, label: t("seg.renovation") },
  ];

  const services = [
    { icon: Thermometer, title: t("svc.heating"), href: "/energie-heizung", n: "01" },
    { icon: Droplet, title: t("svc.hotwater"), href: "/energie-heizung", n: "02" },
    { icon: Sun, title: t("svc.energy"), href: "/energie-heizung", n: "03" },
    { icon: Cpu, title: t("svc.automation"), href: "/gebaeudeautomation", n: "04" },
    { icon: Lightbulb, title: t("svc.lighting"), href: "/gebaeudeautomation", n: "05" },
    { icon: Shield, title: t("svc.access"), href: "/gebaeudeautomation", n: "06" },
    { icon: Zap, title: t("svc.ems"), href: "/energie-heizung", n: "07" },
    { icon: Wrench, title: t("svc.servicecare"), href: "/service", n: "08" },
  ];

  const processSteps = [
    { n: 1, label: t("proc.s1"), d: t("proc.s1d") },
    { n: 2, label: t("proc.s2"), d: t("proc.s2d") },
    { n: 3, label: t("proc.s3"), d: t("proc.s3d") },
    { n: 4, label: t("proc.s4"), d: t("proc.s4d") },
    { n: 5, label: t("proc.s5"), d: t("proc.s5d") },
    { n: 6, label: t("proc.s6"), d: t("proc.s6d") },
    { n: 7, label: t("proc.s7"), d: t("proc.s7d") },
    { n: 8, label: t("proc.s8"), d: t("proc.s8d") },
    { n: 9, label: t("proc.s9"), d: t("proc.s9d") },
    { n: 10, label: t("proc.s10"), d: t("proc.s10d") },
  ];

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section className="relative mt-20 min-h-[calc(100vh-5rem)] flex flex-col justify-end overflow-hidden" data-testid="home-hero-section">
        {/* Background video + overlay */}
        <div className="absolute inset-0 z-0">
          <video
            src={HERO_VIDEO.src}
            poster={HERO_VIDEO.poster}
            autoPlay muted loop playsInline preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="hero-video"
          />
          {/* Subtle vignette — leaves video fully visible, only strengthens readability */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: "radial-gradient(ellipse at 30% 50%, rgba(10,26,46,0.55) 0%, rgba(10,26,46,0.35) 40%, rgba(10,26,46,0.0) 75%)"
          }}/>
          {/* Bottom fade to blend into page */}
          <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{
            background: "linear-gradient(180deg, rgba(10,26,46,0.0) 0%, rgba(10,26,46,0.85) 70%, #0F2A48 100%)"
          }}/>
        </div>

        {/* Content */}
        <div className="relative z-10 site-container w-full pt-20 pb-20 lg:pb-24">
          <div className="reveal-up max-w-5xl">
            {/* Trust counter block */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-[11px] uppercase tracking-[0.18em] font-semibold" data-testid="hero-counter-block">
              <div className="flex items-center gap-2">
                <span className="text-[#5FA637] font-display text-2xl md:text-3xl font-bold tabular-nums leading-none">7</span>
                <span className="text-white/85">Technik-Bereiche</span>
              </div>
              <span className="w-px h-5 bg-white/25"/>
              <div className="flex items-center gap-2">
                <span className="text-[#5FA637] font-display text-2xl md:text-3xl font-bold tabular-nums leading-none">1</span>
                <span className="text-white/85">Partner</span>
              </div>
              <span className="w-px h-5 bg-white/25"/>
              <div className="flex items-center gap-2">
                <span className="text-[#5FA637] font-display text-2xl md:text-3xl font-bold tabular-nums leading-none">10</span>
                <span className="text-white/85">Prozess-Schritte</span>
              </div>
            </div>

            <div className="eyebrow mb-6 text-[#5FA637]">{t("hero.eyebrow")}</div>
            <h1 className="font-display text-[46px] md:text-[72px] lg:text-[88px] leading-[0.98] drop-shadow-[0_6px_20px_rgba(0,0,0,0.5)]" data-testid="hero-headline">
              <span key={`line-${phraseIdx}`} className="block animate-hero-fade">{phrase.line}</span>
              <span key={`italic-${phraseIdx}`} className="italic-accent text-[50px] md:text-[78px] lg:text-[96px] block animate-hero-fade" data-testid="hero-headline-italic">
                {phrase.italic}
              </span>
            </h1>
            <style>{`
              @keyframes hero-fade {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
              .animate-hero-fade { animation: hero-fade 0.7s cubic-bezier(.16,1,.3,1); }
            `}</style>
            <p className="mt-10 max-w-3xl text-base md:text-lg text-white leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link to="/kontakt" className="btn-primary" data-testid="hero-inquiry-btn">
                {t("cta.inquiry")} <ArrowRight size={14}/>
              </Link>
              <Link to="/planung-bim" className="btn-ghost" data-testid="hero-services-btn">
                {t("cta.viewSvc")}
              </Link>
            </div>

            {/* Pills row */}
            <div className="mt-10 text-[11px] tracking-[0.2em] uppercase text-white/55 font-medium">
              {t("hero.pills")}
            </div>

            {/* Segments */}
            <div className="mt-10 flex flex-wrap gap-2">
              {segments.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-[11px] uppercase tracking-[0.1em] text-white/70"
                >
                  <s.icon size={13} strokeWidth={1.6}/>
                  {s.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <button
          onClick={() => window.scrollTo({ top: window.innerHeight * 0.95, behavior: "smooth" })}
          data-testid="hero-scroll-cue"
          aria-label="Weiter scrollen"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/80 hover:text-[#5FA637] transition-colors group"
        >
          <span className="text-[9px] uppercase tracking-[0.3em] font-semibold">Entdecken</span>
          <span className="relative w-6 h-10 border-2 border-white/70 rounded-full flex items-start justify-center p-1.5 group-hover:border-[#5FA637]">
            <span className="w-1 h-2 bg-white/90 rounded-full group-hover:bg-[#5FA637] animate-scroll-dot"/>
          </span>
          <ChevronDown size={14} strokeWidth={2.2} className="animate-bounce-slow"/>
          <style>{`
            @keyframes scroll-dot {
              0%, 20% { transform: translateY(0); opacity: 1; }
              80%, 100% { transform: translateY(10px); opacity: 0; }
            }
            .animate-scroll-dot { animation: scroll-dot 1.8s cubic-bezier(0.4, 0, 0.2, 1) infinite; }
            @keyframes bounce-slow {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(4px); }
            }
            .animate-bounce-slow { animation: bounce-slow 1.8s ease-in-out infinite; }
          `}</style>
        </button>
      </section>

      {/* ============ TESTIMONIALS — direkt unter dem Hero ============ */}
      <Testimonials/>

      {/* ============ INTRO: Technik, die zusammenarbeitet ============ */}
      <section className="site-container section-block">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 reveal">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6 text-[#5FA637]">{t("intro.eyebrow")}</div>
            <h2 className="section-title">
              {t("intro.h2.1")}<br/><span className="italic-accent">{t("intro.h2.italic")}</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-base md:text-lg text-white/75 leading-relaxed">
              {t("intro.body")}
            </p>
            <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-10">
              <FactorCell label={t("intro.factor1")}/>
              <FactorCell label={t("intro.factor2")}/>
              <FactorCell label={t("intro.factor3")}/>
              <FactorCell label={t("intro.factor4")}/>
            </div>
            <Link to="/planung-bim" className="inline-flex items-center gap-2 mt-10 text-[12px] tracking-[0.18em] uppercase font-semibold border-b border-[#5FA637]/60 pb-2 text-[#5FA637] hover:border-[#5FA637]" data-testid="intro-planung-link">
              {t("nav.planungBim")} <ArrowUpRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ REFERENCE GALLERY ============ */}
      <ReferenceGallery/>

      {/* ============ SERVICES GRID ============ */}
      <section className="site-container section-block">
        <div className="mb-12 reveal">
          <div className="eyebrow mb-4 text-[#5FA637]">02 · {t("nav.services")}</div>
          <h2 className="section-title max-w-3xl">
            Alle Bereiche <span className="italic-accent">eines Gebäudes.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 reveal-stagger">
          {services.map((s) => (
            <Link
              to={s.href}
              key={s.n}
              data-testid={`service-${s.n}`}
              className="bg-[#0F2A48] num-card group hover:bg-[#163352]"
            >
              <div className="flex items-start justify-between mb-10">
                <div className="font-display text-2xl text-white/40">{s.n}</div>
                <s.icon size={22} strokeWidth={1.4} className="text-[#5FA637]/70 group-hover:text-[#5FA637] transition-colors"/>
              </div>
              <h3 className="font-display text-lg md:text-xl mb-3 tracking-tight leading-tight">{s.title}</h3>
              <div className="mt-6 text-[11px] tracking-[0.15em] uppercase text-white/40 group-hover:text-[#5FA637] flex items-center gap-2 transition-colors">
                Mehr <ArrowUpRight size={12}/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ============ PROJECT PROCESS (10 steps) ============ */}
      <section className="bg-[#0A1F38]/90 py-20 lg:py-24 border-y border-white/10">
        <div className="site-container">
          <div className="mb-14 reveal">
            <div className="eyebrow mb-4 text-[#5FA637]">03 · {t("proc.eyebrow")}</div>
            <h2 className="section-title max-w-3xl">
              {t("proc.title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 reveal-stagger">
            {processSteps.map((s) => (
              <div key={s.n} className="bg-[#0F2A48] p-6 min-h-[180px] flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="process-dot"/>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 font-semibold">
                    {String(s.n).padStart(2, "0")}
                  </div>
                </div>
                <div className="font-display text-base mb-2 leading-tight">{s.label}</div>
                <div className="text-xs text-white/55 leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE HOUSE ============ */}
      <InteractiveHouse/>

      {/* ============ FEATURED PRODUCTS ============ */}
      {featured.length > 0 && (
        <section className="site-container section-block">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12 reveal">
            <div>
              <div className="eyebrow mb-4 text-[#5FA637]">04 · {t("nav.products")}</div>
              <h2 className="section-title">
                Technik und <span className="italic-accent">Material.</span>
              </h2>
            </div>
            <Link to="/shop" className="btn-ghost" data-testid="all-products-btn">
              {t("cta.discover")} <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/10 border border-white/10 reveal-stagger">
            {featured.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* ============ ENERGY VORCHECK ============ */}
      <section className="site-container section-block">
        <div className="mb-12 reveal">
          <div className="eyebrow mb-4 text-[#5FA637]">05 · {t("energy.eyebrow")}</div>
          <h2 className="section-title max-w-3xl">
            {t("energy.h1.1")} <span className="italic-accent">{t("energy.h1.italic")}</span> {t("energy.h1.2")}
          </h2>
          <p className="text-sm text-white/55 italic mt-4 max-w-2xl">{t("energy.intro")}</p>
        </div>
        <EnergyCalculator/>
      </section>

      {/* ============ FAQ ============ */}
      <section className="site-container section-block">
        <div className="mb-12 text-center reveal">
          <div className="eyebrow mb-4 text-[#5FA637]">06 · FAQ</div>
          <h2 className="section-title">
            Häufig gestellte <span className="italic-accent">Fragen.</span>
          </h2>
        </div>
        <FAQ/>
      </section>

      {/* ============ CTA ============ */}
      <section className="site-container section-block">
        <div className="cta-panel text-center">
          <div className="eyebrow mb-6 text-[#5FA637]">07 · {t("cta.inquiry")}</div>
          <h2 className="section-title max-w-4xl mx-auto">
            Ihr Projekt beginnt<br/><span className="italic-accent">mit einer Analyse.</span>
          </h2>
          <p className="mt-8 text-white/70 max-w-xl mx-auto leading-relaxed">
            Beschreiben Sie Ihr Vorhaben kurz. Wir melden uns persönlich — ohne pauschale Versprechen.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link to="/kontakt" className="btn-primary" data-testid="cta-contact-btn">
              {t("cta.inquiry")} <ArrowRight size={14}/>
            </Link>
            <Link to="/shop" className="btn-ghost" data-testid="cta-shop-btn">
              {t("cta.discover")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function FactorCell({ label }) {
  return (
    <div className="bg-[#0F2A48] p-5 flex items-center gap-3">
      <div className="w-1.5 h-1.5 rounded-full bg-[#5FA637]"/>
      <div className="text-xs text-white/75 leading-tight">{label}</div>
    </div>
  );
}
